import React, { useState, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Separator } from '../components/ui/Separator';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { format, formatDistanceToNow, differenceInDays } from 'date-fns';
import { useApp } from '../context/AppContext';

export const Alerts = () => {
  const { data } = useApp();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [viewMode, setViewMode] = useState('active');

  // Extended alerts data with more detail
  const extendedAlerts = [
    {
      id: 1,
      type: 'breach',
      severity: 'critical',
      title: 'PPU Non-Compliance Acknowledgment Overdue',
      message: 'Ministry of Works failed to acknowledge non-compliance notice within 7 days.',
      entity: 'Ministry of Works & Transport',
      entityCode: 'MWT',
      daysOverdue: 12,
      legalBasis: 'Public Procurement Act S.7(4)',
      projectId: 'NAM-2025-0012',
      projectName: 'Windhoek-Okahandja Dual Carriageway Section 4',
      raisedBy: 'Public Procurement Unit',
      raisedDate: '2025-01-08T09:00:00Z',
      dueDate: '2025-01-15T17:00:00Z',
      status: 'escalated',
      assignedTo: 'Permanent Secretary - Works & Transport',
      escalationLevel: 2,
      actionRequired: 'Formal ministerial intervention required',
      potentialImpact: 'Contract suspension, legal proceedings',
      relatedDocuments: ['NON_COMPLIANCE_NOTICE_001.pdf', 'CONTRACT_NAM_2025_0012.pdf'],
      timeline: [
        { date: '2025-01-08T09:00:00Z', action: 'Non-compliance notice issued by PPU', actor: 'PPU Compliance Officer' },
        { date: '2025-01-15T17:00:00Z', action: 'Acknowledgment deadline passed', actor: 'System' },
        { date: '2025-01-16T10:30:00Z', action: 'Auto-escalated to Level 1 (Directorate)', actor: 'GovTrack System' },
        { date: '2025-01-20T14:00:00Z', action: 'Escalated to Level 2 (Permanent Secretary)', actor: "Prime Minister's Office" }
      ],
      isActive: true
    },
    {
      id: 2,
      type: 'deadline',
      severity: 'high',
      title: 'NDP6 Quarterly Report Submission Late',
      message: 'Ministry of Education has not submitted Q4 2024 progress report.',
      entity: 'Ministry of Education, Arts & Culture',
      entityCode: 'MOE',
      daysOverdue: 5,
      legalBasis: 'NDP6 IMEP Framework',
      projectId: null,
      projectName: null,
      raisedBy: 'National Planning Commission',
      raisedDate: '2025-01-10T08:00:00Z',
      dueDate: '2025-01-15T23:59:00Z',
      status: 'pending',
      assignedTo: 'Director of Planning - MOE',
      escalationLevel: 1,
      actionRequired: 'Submit consolidated quarterly report',
      potentialImpact: 'Budget release delays, performance rating impact',
      relatedDocuments: ['NDP6_Q4_TEMPLATE.xlsx'],
      timeline: [
        { date: '2024-12-31T23:59:00Z', action: 'Q4 2024 reporting period ended', actor: 'System' },
        { date: '2025-01-15T23:59:00Z', action: 'Report submission deadline passed', actor: 'System' },
        { date: '2025-01-16T09:00:00Z', action: 'Alert raised and assigned to MOE Director', actor: 'NPC Monitoring Unit' }
      ],
      isActive: true
    },
    {
      id: 3,
      type: 'risk',
      severity: 'critical',
      title: 'Project Abandonment Risk Detected',
      message: 'No activity logged for 45 days on School Renovation Program: Kunene.',
      entity: 'Ministry of Education, Arts & Culture',
      entityCode: 'MOE',
      daysOverdue: 45,
      legalBasis: 'Contract Clause 14.2 - Suspension & Termination',
      projectId: 'NAM-2023-0099',
      projectName: 'School Renovation Program: Kunene Region',
      raisedBy: 'GovTrack Monitoring System',
      raisedDate: '2025-01-18T06:00:00Z',
      dueDate: null,
      status: 'under_investigation',
      assignedTo: 'Project Manager - MOE Infrastructure',
      escalationLevel: 2,
      actionRequired: 'Site inspection and contractor engagement',
      potentialImpact: 'Contract termination, contractor debarment, budget reallocation',
      relatedDocuments: ['CONTRACT_NAM_2023_0099.pdf', 'SITE_INSPECTION_LOG.pdf'],
      timeline: [
        { date: '2024-12-03T00:00:00Z', action: 'Last activity recorded (material delivery)', actor: 'Site Supervisor' },
        { date: '2025-01-17T06:00:00Z', action: '45-day inactivity threshold breached', actor: 'System' },
        { date: '2025-01-18T06:00:00Z', action: 'Critical alert raised', actor: 'GovTrack System' },
        { date: '2025-01-18T14:30:00Z', action: 'Investigation initiated', actor: "Prime Minister's Office" }
      ],
      isActive: true
    },
    // RESOLVED/HISTORICAL ALERTS
    {
      id: 101,
      type: 'deadline',
      severity: 'medium',
      title: 'Annual Procurement Plan Submission Overdue',
      message: 'Ministry of Agriculture submitted annual plan 3 days late.',
      entity: 'Ministry of Agriculture, Water & Land Reform',
      entityCode: 'MAF',
      daysOverdue: 0,
      legalBasis: 'Public Procurement Act S.22(1)',
      projectId: null,
      projectName: null,
      raisedBy: 'Public Procurement Unit',
      raisedDate: '2024-12-13T09:00:00Z',
      dueDate: '2024-12-10T23:59:00Z',
      status: 'resolved',
      assignedTo: 'Chief Procurement Officer - MAF',
      escalationLevel: 1,
      actionRequired: 'Submit annual procurement plan to PPU portal',
      potentialImpact: 'Procurement freeze, delayed project approvals',
      relatedDocuments: ['APP_TEMPLATE_FY2025_26.xlsx', 'PPU_GUIDELINES.pdf'],
      timeline: [
        { date: '2024-12-10T23:59:00Z', action: 'Submission deadline passed', actor: 'System' },
        { date: '2024-12-13T09:00:00Z', action: 'Alert raised and sent to MAF', actor: 'PPU' },
        { date: '2024-12-13T14:20:00Z', action: 'Acknowledged by MAF Chief Procurement Officer', actor: 'J. Shaanika (MAF)' },
        { date: '2024-12-13T16:45:00Z', action: 'Annual Procurement Plan submitted', actor: 'MAF' },
        { date: '2024-12-14T10:00:00Z', action: 'Alert resolved and closed', actor: 'PPU' }
      ],
      resolvedDate: '2024-12-14T10:00:00Z',
      resolvedBy: 'Public Procurement Unit',
      resolutionNotes: 'Annual Procurement Plan submitted and approved. Ministry reminded of deadline compliance requirements.',
      isActive: false
    },
    {
      id: 102,
      type: 'compliance',
      severity: 'high',
      title: 'Environmental Clearance Certificate Expired',
      message: 'Green Scheme Irrigation project environmental clearance expired.',
      entity: 'Ministry of Agriculture, Water & Land Reform',
      entityCode: 'MAF',
      daysOverdue: 0,
      legalBasis: 'Environmental Management Act S.27',
      projectId: 'NAM-2024-0882',
      projectName: 'Green Scheme Irrigation Expansion',
      raisedBy: 'Ministry of Environment, Forestry & Tourism',
      raisedDate: '2024-11-20T08:00:00Z',
      dueDate: '2024-11-25T23:59:00Z',
      status: 'resolved',
      assignedTo: 'Environmental Officer - MAF',
      escalationLevel: 2,
      actionRequired: 'Submit renewal application to MEFT',
      potentialImpact: 'Project suspension, legal non-compliance',
      relatedDocuments: ['ENVIRONMENTAL_CLEARANCE_2024.pdf'],
      timeline: [
        { date: '2024-11-15T08:00:00Z', action: '10-day expiry warning triggered', actor: 'MEFT Compliance System' },
        { date: '2024-11-20T08:00:00Z', action: 'Critical alert raised', actor: 'MEFT' },
        { date: '2024-11-21T10:00:00Z', action: 'Renewal application submitted', actor: 'MAF Environmental Officer' },
        { date: '2024-11-23T14:30:00Z', action: 'New clearance certificate issued', actor: 'MEFT' },
        { date: '2024-11-23T15:00:00Z', action: 'Alert resolved', actor: 'MEFT Compliance System' }
      ],
      resolvedDate: '2024-11-23T15:00:00Z',
      resolvedBy: 'Ministry of Environment, Forestry & Tourism',
      resolutionNotes: 'Environmental clearance renewed for 12 months. Project authorized to continue.',
      isActive: false
    }
  ];

  // Generate alerts from overdue/stalled cabinet decisions
  const cabinetAlerts = (data.cabinetDecisions ?? [])
    .filter(d => d.status === 'Overdue' || d.status === 'Stalled')
    .map(d => {
      const daysOver = d.deadline ? differenceInDays(new Date(), new Date(d.deadline)) : 0;
      const severity = daysOver > 30 ? 'critical' : daysOver > 14 ? 'high' : 'medium';
      const leadMinistry = d.responsibleMinistries?.find(m => m.role === 'Lead');

      return {
        id: 2000 + parseInt(d.id?.replace(/\D/g, '') || '0'),
        type: 'cabinet_decision',
        severity,
        title: `Cabinet Decision ${d.status}: ${d.title}`,
        message: `Implementation deadline of ${d.deadline} has passed. ${d.overdueDirectives ?? 0} directive(s) overdue.`,
        entity: leadMinistry?.ministryName ?? d.originatingMinistry ?? 'Unknown Ministry',
        entityCode: d.leadMinistryId ?? '',
        daysOverdue: Math.max(daysOver, 0),
        legalBasis: d.legalBasis ?? 'Cabinet Resolution',
        projectId: d.linkedProjectIds?.[0] ?? null,
        projectName: null,
        raisedBy: "Prime Minister's Office",
        raisedDate: d.communicatedDate ? new Date(d.communicatedDate).toISOString() : new Date().toISOString(),
        dueDate: d.deadline ? new Date(d.deadline).toISOString() : null,
        status: 'pending',
        assignedTo: leadMinistry?.ministryName ?? 'Responsible Ministry',
        escalationLevel: d.escalationLevel ?? 0,
        actionRequired: `Address overdue cabinet decision implementation — ${d.overdueDirectives ?? 0} directive(s) require immediate attention`,
        potentialImpact: 'Escalation to Prime Minister, parliamentary oversight referral',
        relatedDocuments: [],
        timeline: [],
        isActive: true,
        link: `/cabinet-decisions/${d.id}`
      };
    });

  // Merge cabinet alerts into the static alerts list
  const allAlerts = [...extendedAlerts, ...cabinetAlerts];

  const severityConfig = {
    critical: {
      color: 'red',
      icon: FiIcons.FiAlertOctagon,
      label: 'Critical',
      bgClass: 'bg-gradient-to-br from-red-50 to-red-100/50',
      borderClass: 'border-red-300',
      textClass: 'text-red-700',
      iconBgClass: 'bg-gradient-to-br from-red-500 to-red-600',
      glowClass: 'shadow-red-200/50'
    },
    high: {
      color: 'orange',
      icon: FiIcons.FiAlertTriangle,
      label: 'High',
      bgClass: 'bg-gradient-to-br from-orange-50 to-orange-100/50',
      borderClass: 'border-orange-300',
      textClass: 'text-orange-700',
      iconBgClass: 'bg-gradient-to-br from-orange-500 to-orange-600',
      glowClass: 'shadow-orange-200/50'
    },
    medium: {
      color: 'amber',
      icon: FiIcons.FiAlertCircle,
      label: 'Medium',
      bgClass: 'bg-gradient-to-br from-amber-50 to-amber-100/50',
      borderClass: 'border-amber-300',
      textClass: 'text-amber-700',
      iconBgClass: 'bg-gradient-to-br from-amber-500 to-amber-600',
      glowClass: 'shadow-amber-200/50'
    },
    low: {
      color: 'blue',
      icon: FiIcons.FiInfo,
      label: 'Low',
      bgClass: 'bg-gradient-to-br from-blue-50 to-blue-100/50',
      borderClass: 'border-blue-300',
      textClass: 'text-blue-700',
      iconBgClass: 'bg-gradient-to-br from-blue-500 to-blue-600',
      glowClass: 'shadow-blue-200/50'
    }
  };

  const statusConfig = {
    pending: { label: 'Pending Action', color: 'slate', icon: FiIcons.FiClock },
    acknowledged: { label: 'Acknowledged', color: 'blue', icon: FiIcons.FiCheckCircle },
    under_investigation: { label: 'Under Investigation', color: 'amber', icon: FiIcons.FiSearch },
    escalated: { label: 'Escalated', color: 'red', icon: FiIcons.FiArrowUp },
    resolved: { label: 'Resolved', color: 'green', icon: FiIcons.FiCheck },
    dismissed: { label: 'Dismissed', color: 'slate', icon: FiIcons.FiX }
  };

  const typeConfig = {
    breach: { label: 'Legal Breach', icon: FiIcons.FiShield },
    deadline: { label: 'Deadline', icon: FiIcons.FiClock },
    risk: { label: 'Risk Alert', icon: FiIcons.FiAlertTriangle },
    compliance: { label: 'Compliance', icon: FiIcons.FiCheckSquare },
    financial: { label: 'Financial', icon: FiIcons.FiDollarSign },
    audit: { label: 'Audit', icon: FiIcons.FiFileText },
    contractor: { label: 'Contractor', icon: FiIcons.FiUsers },
    cabinet_decision: { label: 'Cabinet Decision', icon: FiIcons.FiBookOpen }
  };

  const { filteredAlerts, stats } = useMemo(() => {
    let filtered = allAlerts;

    // Filter by view mode
    if (viewMode === 'active') {
      filtered = filtered.filter(a => a.isActive);
    } else if (viewMode === 'history') {
      filtered = filtered.filter(a => !a.isActive);
    }

    if (activeTab !== 'all') {
      if (activeTab === 'critical') {
        filtered = filtered.filter(a => a.severity === 'critical');
      } else if (activeTab === 'overdue') {
        filtered = filtered.filter(a => a.daysOverdue > 0);
      } else if (activeTab === 'escalated') {
        filtered = filtered.filter(a => a.status === 'escalated');
      } else {
        filtered = filtered.filter(a => a.type === activeTab);
      }
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.title.toLowerCase().includes(query) ||
        a.message.toLowerCase().includes(query) ||
        a.entity.toLowerCase().includes(query) ||
        a.entityCode.toLowerCase().includes(query) ||
        (a.projectId && a.projectId.toLowerCase().includes(query)) ||
        a.legalBasis.toLowerCase().includes(query)
      );
    }

    filtered.sort((a, b) => {
      const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[a.severity] - severityOrder[b.severity];
      }
      return b.daysOverdue - a.daysOverdue;
    });

    const stats = {
      total: allAlerts.length,
      active: allAlerts.filter(a => a.isActive).length,
      resolved: allAlerts.filter(a => !a.isActive).length,
      critical: allAlerts.filter(a => a.severity === 'critical' && a.isActive).length,
      high: allAlerts.filter(a => a.severity === 'high' && a.isActive).length,
      overdue: allAlerts.filter(a => a.daysOverdue > 0 && a.isActive).length,
      escalated: allAlerts.filter(a => a.status === 'escalated' && a.isActive).length,
      pending: allAlerts.filter(a => a.status === 'pending' && a.isActive).length
    };

    return { filteredAlerts: filtered, stats };
  }, [activeTab, searchQuery, viewMode, allAlerts]);

  const tabs = [
    { id: 'all', label: 'All Alerts', count: viewMode === 'active' ? stats.active : viewMode === 'history' ? stats.resolved : stats.total, icon: FiIcons.FiList },
    { id: 'critical', label: 'Critical', count: stats.critical, icon: FiIcons.FiAlertOctagon },
    { id: 'overdue', label: 'Overdue', count: stats.overdue, icon: FiIcons.FiClock },
    { id: 'escalated', label: 'Escalated', count: stats.escalated, icon: FiIcons.FiArrowUp },
    { id: 'breach', label: 'Legal Breaches', count: allAlerts.filter(a => a.type === 'breach' && (viewMode === 'all' || (viewMode === 'active' ? a.isActive : !a.isActive))).length, icon: FiIcons.FiShield },
    { id: 'cabinet_decision', label: 'Cabinet Decisions', count: allAlerts.filter(a => a.type === 'cabinet_decision' && (viewMode === 'all' || (viewMode === 'active' ? a.isActive : !a.isActive))).length, icon: FiIcons.FiBookOpen },
    { id: 'audit', label: 'Audit Issues', count: allAlerts.filter(a => a.type === 'audit' && (viewMode === 'all' || (viewMode === 'active' ? a.isActive : !a.isActive))).length, icon: FiIcons.FiFileText }
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header with gradient background */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-grn-navy via-blue-900 to-slate-900 p-8 shadow-2xl">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtNi42MjcgNS4zNzMtMTIgMTItMTJzMTIgNS4zNzMgMTIgMTItNS4zNzMgMTItMTIgMTItMTItNS4zNzMtMTItMTJ6bS0xMiAwYzAtNi42MjcgNS4zNzMtMTIgMTItMTJzMTIgNS4zNzMgMTIgMTItNS4zNzMgMTItMTIgMTItMTItNS4zNzMtMTItMTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
                <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-3xl text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-black text-white tracking-tight">Alerts & Escalations</h1>
                <p className="text-blue-200 mt-1 text-sm font-medium">
                  Real-time monitoring of compliance breaches, deadlines, and critical issues
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" size="default" className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20">
              <SafeIcon icon={FiIcons.FiFilter} />
              Advanced Filters
            </Button>
            <Button variant="outline" size="default" className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20">
              <SafeIcon icon={FiIcons.FiDownload} />
              Export Report
            </Button>
            <Button variant="default" size="default" className="bg-white text-grn-navy hover:bg-blue-50 shadow-lg shadow-white/20">
              <SafeIcon icon={FiIcons.FiPlus} />
              Create Alert
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Dashboard with enhanced cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="text-center p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-t-slate-400 bg-gradient-to-br from-white to-slate-50">
          <div className="text-4xl font-black text-foreground mb-2">{stats.total}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Total Alerts</div>
        </Card>
        <Card className="text-center p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-t-green-500 bg-gradient-to-br from-white to-green-50">
          <div className="text-4xl font-black text-green-700 mb-2">{stats.active}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Active</div>
        </Card>
        <Card className="text-center p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-t-blue-500 bg-gradient-to-br from-white to-blue-50">
          <div className="text-4xl font-black text-blue-700 mb-2">{stats.resolved}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Resolved</div>
        </Card>
        <Card className="text-center p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-t-red-600 bg-gradient-to-br from-white to-red-50 animate-pulse">
          <div className="text-4xl font-black text-red-700 mb-2">{stats.critical}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Critical</div>
        </Card>
        <Card className="text-center p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-t-amber-500 bg-gradient-to-br from-white to-amber-50">
          <div className="text-4xl font-black text-amber-700 mb-2">{stats.overdue}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Overdue</div>
        </Card>
        <Card className="text-center p-6 hover:shadow-xl transition-all duration-300 border-t-4 border-t-orange-500 bg-gradient-to-br from-white to-orange-50">
          <div className="text-4xl font-black text-orange-700 mb-2">{stats.escalated}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Escalated</div>
        </Card>
      </div>

      {/* View Mode Selector with enhanced styling */}
      <Card className="bg-gradient-to-br from-white to-slate-50 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">View Mode</h3>
          <div className="flex gap-2">
            {[
              { id: 'active', label: 'Active Alerts', icon: FiIcons.FiAlertCircle, count: stats.active },
              { id: 'history', label: 'Escalation History', icon: FiIcons.FiClock, count: stats.resolved },
              { id: 'all', label: 'All Alerts', icon: FiIcons.FiList, count: stats.total }
            ].map(mode => (
              <Button
                key={mode.id}
                onClick={() => setViewMode(mode.id)}
                variant={viewMode === mode.id ? 'default' : 'outline'}
                size="default"
                className={viewMode === mode.id ? 'shadow-lg' : ''}
              >
                <SafeIcon icon={mode.icon} />
                {mode.label}
                <span className={clsx(
                  'ml-2 px-2.5 py-0.5 rounded-full text-xs font-bold',
                  viewMode === mode.id ? 'bg-white/20' : 'bg-muted'
                )}>
                  {mode.count}
                </span>
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Tabs with enhanced styling */}
      <Card className="bg-gradient-to-br from-white to-slate-50 shadow-lg">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-bold text-foreground mb-3 uppercase tracking-wider">Filter by Category</h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {tabs.map(tab => (
                <Button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  variant={activeTab === tab.id ? 'default' : 'outline'}
                  size="sm"
                  className={clsx(
                    'whitespace-nowrap transition-all duration-300',
                    activeTab === tab.id && 'shadow-lg scale-105'
                  )}
                >
                  <SafeIcon icon={tab.icon} />
                  {tab.label}
                  <span className={clsx(
                    'ml-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold',
                    activeTab === tab.id ? 'bg-white/20' : 'bg-muted'
                  )}>
                    {tab.count}
                  </span>
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          <div className="relative">
            <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-3 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search alerts by title, entity, project ID, or legal basis..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base shadow-sm"
            />
          </div>
        </div>
      </Card>

      {/* Results Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-sm font-bold text-foreground uppercase tracking-wider">
          Showing {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? 's' : ''}
          {viewMode === 'history' && ' (Resolved)'}
        </h2>
        <Select className="w-[250px] shadow-sm">
          <option>Sort by: Severity (Critical First)</option>
          <option>Sort by: Days Overdue</option>
          <option>Sort by: Date Raised</option>
          <option>Sort by: Entity</option>
        </Select>
      </div>

      {/* Alert Cards with premium styling */}
      {filteredAlerts.length > 0 ? (
        <div className="space-y-4">
          {filteredAlerts.map(alert => {
            const severityInfo = severityConfig[alert.severity];
            const statusInfo = statusConfig[alert.status];
            const typeInfo = typeConfig[alert.type];

            return (
              <Card
                key={alert.id}
                className={clsx(
                  'hover:shadow-2xl transition-all duration-300 border-l-4 group',
                  severityInfo.borderClass,
                  severityInfo.glowClass,
                  !alert.isActive && 'opacity-75',
                  'hover:scale-[1.01]'
                )}
              >
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4 flex-1">
                      <div className={clsx(
                        'p-4 rounded-xl shadow-lg transition-all duration-300 group-hover:scale-110',
                        severityInfo.iconBgClass
                      )}>
                        <SafeIcon
                          icon={severityInfo.icon}
                          className="text-3xl text-white"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <Badge status={severityInfo.label} size="sm" className="shadow-sm" />
                          <span className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm">
                            <SafeIcon icon={typeInfo.icon} className="text-xs" />
                            {typeInfo.label}
                          </span>
                          <Badge status={statusInfo.label} size="sm" className="shadow-sm" />
                          {alert.escalationLevel > 0 && alert.isActive && (
                            <span className="px-3 py-1.5 bg-destructive/10 text-destructive rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm border border-destructive/20">
                              <SafeIcon icon={FiIcons.FiArrowUp} className="text-xs" />
                              Level {alert.escalationLevel}
                            </span>
                          )}
                          {alert.daysOverdue > 0 && alert.isActive && (
                            <span className="px-3 py-1.5 bg-destructive text-destructive-foreground rounded-full text-xs font-bold animate-pulse shadow-lg">
                              {alert.daysOverdue} DAYS OVERDUE
                            </span>
                          )}
                          {!alert.isActive && (
                            <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm border border-green-200">
                              <SafeIcon icon={FiIcons.FiCheckCircle} className="text-xs" />
                              RESOLVED
                            </span>
                          )}
                        </div>

                        <h3 className="text-xl font-black text-foreground mb-3 leading-tight">{alert.title}</h3>
                        <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{alert.message}</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                            <SafeIcon icon={FiIcons.FiBuilding} className="text-muted-foreground mt-0.5" />
                            <div>
                              <span className="text-muted-foreground font-bold">Entity:</span>
                              <p className="font-bold text-foreground mt-1">{alert.entity}</p>
                            </div>
                          </div>
                          {alert.projectId && (
                            <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                              <SafeIcon icon={FiIcons.FiBriefcase} className="text-muted-foreground mt-0.5" />
                              <div>
                                <span className="text-muted-foreground font-bold">Project:</span>
                                <p className="font-mono text-foreground font-bold mt-1">{alert.projectId}</p>
                                <p className="text-muted-foreground mt-1">{alert.projectName}</p>
                              </div>
                            </div>
                          )}
                          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                            <SafeIcon icon={FiIcons.FiBook} className="text-muted-foreground mt-0.5" />
                            <div>
                              <span className="text-muted-foreground font-bold">Legal Basis:</span>
                              <p className="font-mono text-foreground font-bold mt-1">{alert.legalBasis}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                            <SafeIcon icon={FiIcons.FiUser} className="text-muted-foreground mt-0.5" />
                            <div>
                              <span className="text-muted-foreground font-bold">Assigned To:</span>
                              <p className="text-foreground font-bold mt-1">{alert.assignedTo}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Resolution Info (for resolved alerts) */}
                  {!alert.isActive && alert.resolvedDate && (
                    <div className="p-5 bg-gradient-to-br from-green-50 to-green-100/50 border border-green-300 rounded-xl shadow-sm">
                      <div className="flex items-start gap-3">
                        <SafeIcon icon={FiIcons.FiCheckCircle} className="text-green-600 text-2xl mt-0.5" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-black text-green-900 uppercase tracking-wider">Alert Resolved</h4>
                            <span className="text-xs text-green-700 font-bold">
                              {format(new Date(alert.resolvedDate), 'dd MMM yyyy, HH:mm')}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3 text-xs">
                            <div className="p-3 bg-white/50 rounded-lg">
                              <span className="text-green-700 font-bold">Resolved By:</span>
                              <p className="text-green-900 font-bold mt-1">{alert.resolvedBy}</p>
                            </div>
                            <div className="p-3 bg-white/50 rounded-lg">
                              <span className="text-green-700 font-bold">Time to Resolution:</span>
                              <p className="text-green-900 font-bold mt-1">
                                {formatDistanceToNow(new Date(alert.raisedDate), { addSuffix: false })}
                              </p>
                            </div>
                          </div>
                          {alert.resolutionNotes && (
                            <div className="p-3 bg-white/50 rounded-lg">
                              <span className="text-xs text-green-700 font-bold">Resolution Notes:</span>
                              <p className="text-sm text-green-900 mt-2 italic leading-relaxed">"{alert.resolutionNotes}"</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Required (for active alerts) */}
                  {alert.isActive && (
                    <div className={clsx(
                      'p-5 rounded-xl border shadow-sm',
                      alert.severity === 'critical' ? 'bg-gradient-to-br from-red-50 to-red-100/50 border-red-300' :
                      alert.severity === 'high' ? 'bg-gradient-to-br from-orange-50 to-orange-100/50 border-orange-300' :
                      'bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-300'
                    )}>
                      <div className="flex items-start gap-3">
                        <SafeIcon icon={FiIcons.FiAlertCircle} className={clsx('text-2xl', severityInfo.textClass)} />
                        <div className="flex-1">
                          <p className="text-xs font-black text-muted-foreground uppercase tracking-wider mb-2">
                            Action Required
                          </p>
                          <p className="text-sm text-foreground font-bold leading-relaxed">{alert.actionRequired}</p>
                          {alert.potentialImpact && (
                            <p className="text-xs text-muted-foreground mt-3 italic leading-relaxed p-3 bg-white/50 rounded-lg">
                              <strong>Potential Impact:</strong> {alert.potentialImpact}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Timeline Preview */}
                  {alert.timeline && alert.timeline.length > 0 && (
                    <div className="pt-4 border-t border-border">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedAlert(selectedAlert?.id === alert.id ? null : alert)}
                        className="hover:bg-muted/50"
                      >
                        <SafeIcon icon={FiIcons.FiClock} />
                        {selectedAlert?.id === alert.id ? 'Hide' : 'View'} Timeline ({alert.timeline.length} events)
                        <SafeIcon icon={selectedAlert?.id === alert.id ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} />
                      </Button>

                      {selectedAlert?.id === alert.id && (
                        <div className="mt-4 relative pl-8 border-l-2 border-border space-y-4">
                          {alert.timeline.map((event, idx) => (
                            <div key={idx} className="relative">
                              <span className="absolute -left-[33px] top-1 h-4 w-4 rounded-full bg-primary border-4 border-card shadow-lg"></span>
                              <div className="text-xs p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                                <p className="font-bold text-foreground">{event.action}</p>
                                <div className="flex items-center gap-2 mt-2 text-muted-foreground">
                                  <span className="font-bold">{format(new Date(event.date), 'dd MMM yyyy, HH:mm')}</span>
                                  <span>•</span>
                                  <span>{event.actor}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-border">
                    <Button variant="outline" className="flex-1 shadow-sm hover:shadow-md transition-all">
                      <SafeIcon icon={FiIcons.FiEye} />
                      View Full Details
                    </Button>
                    {alert.isActive && alert.status === 'pending' && (
                      <Button variant="secondary" className="flex-1 shadow-sm hover:shadow-md transition-all">
                        <SafeIcon icon={FiIcons.FiCheckCircle} />
                        Acknowledge
                      </Button>
                    )}
                    {alert.isActive && (alert.severity === 'critical' || alert.severity === 'high') && alert.status !== 'escalated' && (
                      <Button variant="destructive" className="flex-1 shadow-lg hover:shadow-xl transition-all">
                        <SafeIcon icon={FiIcons.FiArrowUp} />
                        Escalate
                      </Button>
                    )}
                    {alert.relatedDocuments && alert.relatedDocuments.length > 0 && (
                      <Button variant="outline" className="shadow-sm hover:shadow-md transition-all">
                        <SafeIcon icon={FiIcons.FiPaperclip} />
                        {alert.relatedDocuments.length} Doc{alert.relatedDocuments.length !== 1 ? 's' : ''}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="text-center py-16 bg-gradient-to-br from-white to-slate-50 shadow-lg">
          <div className="flex flex-col items-center justify-center text-muted-foreground">
            <div className="p-6 bg-muted/30 rounded-full mb-4">
              <SafeIcon icon={FiIcons.FiCheckCircle} className="text-6xl" />
            </div>
            <h3 className="text-xl font-black text-foreground mb-2">No alerts found</h3>
            <p className="text-sm">Try adjusting your filters or search query</p>
          </div>
        </Card>
      )}

      {/* Legal Notice with enhanced styling */}
      <Card className="bg-gradient-to-br from-grn-navy to-blue-900 text-white shadow-2xl">
        <div className="flex gap-4">
          <div className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shrink-0">
            <SafeIcon icon={FiIcons.FiInfo} className="text-2xl" />
          </div>
          <div className="text-xs leading-relaxed">
            <p className="font-black mb-3 text-base uppercase tracking-wider">Alert System Legal Framework</p>
            <p className="opacity-90">
              All alerts are automatically logged and tracked in the National Compliance System. Alerts are generated based on statutory deadlines and regulatory requirements under the <strong>Public Procurement Act (2015)</strong>, <strong>State Finance Act (1991)</strong>, <strong>NDP6 IMEP Framework</strong>, and other relevant legislation. Failure to address critical alerts may result in escalation to parliamentary oversight committees. Resolved alerts are maintained in the permanent audit trail for accountability and transparency.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};